export const ISTANBUL_DISTRICTS = [
  "Adalar",
  "Arnavutkoy",
  "Atasehir",
  "Avcilar",
  "Bagcilar",
  "Bahcelievler",
  "Bakirkoy",
  "Basaksehir",
  "Bayrampasa",
  "Besiktas",
  "Beykoz",
  "Beylikduzu",
  "Beyoglu",
  "Buyukcekmece",
  "Catalca",
  "Cekmekoy",
  "Esenler",
  "Esenyurt",
  "Eyupsultan",
  "Fatih",
  "Gaziosmanpasa",
  "Gungoren",
  "Kadikoy",
  "Kagithane",
  "Kartal",
  "Kucukcekmece",
  "Maltepe",
  "Pendik",
  "Sancaktepe",
  "Sariyer",
  "Silivri",
  "Sisli",
  "Sultanbeyli",
  "Sultangazi",
  "Tuzla",
  "Umraniye",
  "Uskudar",
  "Zeytinburnu",
];

const DISTRICTS_BY_KEY = ISTANBUL_DISTRICTS.map((district) => ({
  district,
  key: normalizeDistrictKey(district),
})).sort((a, b) => b.key.length - a.key.length);

const ISTANBUL_CITY_KEYS = new Set([
  "istanbul",
  "istanbulprovince",
  "istanbulili",
  "turkiye",
  "turkey",
]);

export function normalizeDistrictKey(text = "") {
  return String(text)
    .trim()
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9]/g, "");
}

function isIstanbulCityLabel(text) {
  const key = normalizeDistrictKey(text);
  return !key || ISTANBUL_CITY_KEYS.has(key) || key.startsWith("istanbul");
}

function matchDistrictInText(text) {
  const normalized = normalizeDistrictKey(text);
  if (!normalized) return null;

  for (const { district, key } of DISTRICTS_BY_KEY) {
    if (normalized.includes(key)) return district;
  }
  return null;
}

export function getAddressText(value) {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object") {
    const candidates = [
      value.manual_address,
      value.label,
      value.address,
      value.full_address,
      value.open_address,
      value.name,
    ];
    const firstText = candidates.find(
      (item) => typeof item === "string" && item.trim(),
    );
    return firstText ? firstText.trim() : "";
  }
  return String(value).trim();
}

export function resolveShopAddress(row) {
  return (
    getAddressText(row?.address) ||
    getAddressText(row?.shop_address) ||
    getAddressText(row?.location) ||
    getAddressText(row?.vendor_address) ||
    getAddressText(row?.manual_address)
  );
}

function parseDistrictFromFormattedAddress(rawAddress) {
  if (!rawAddress) return null;

  if (rawAddress.includes("/")) {
    const slashParts = rawAddress
      .split("/")
      .map((part) => part.trim())
      .filter(Boolean);
    const beforeCity =
      slashParts[slashParts.length - 2] || slashParts[0] || "";
    const beforeCityParts = beforeCity
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    const tail = beforeCityParts[beforeCityParts.length - 1] || beforeCity;
    const fromTail = matchDistrictInText(tail);
    if (fromTail) return fromTail;
  }

  const commaParts = rawAddress
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  for (let i = commaParts.length - 1; i >= 0; i -= 1) {
    const fromPart = matchDistrictInText(commaParts[i]);
    if (fromPart) return fromPart;
  }

  return matchDistrictInText(rawAddress);
}

export function resolveShopDistrict(row) {
  const addressObj =
    row?.address && typeof row.address === "object" ? row.address : null;
  const addressText = resolveShopAddress(row);

  if (addressObj) {
    const objectCandidates = [
      addressObj.district,
      addressObj.town,
      addressObj.sublocality,
      addressObj.city,
      addressObj.state,
      addressObj.province,
    ];
    for (const candidate of objectCandidates) {
      if (typeof candidate !== "string" || !candidate.trim()) continue;
      if (isIstanbulCityLabel(candidate)) continue;
      const fromObject = matchDistrictInText(candidate);
      if (fromObject) return fromObject;
    }
  }

  const fromFormatted = parseDistrictFromFormattedAddress(addressText);
  if (fromFormatted) return fromFormatted;

  return matchDistrictInText(addressText);
}

export function shopMatchesDistrictFilter(row, filterDistrict) {
  if (!filterDistrict || filterDistrict === "all") return true;
  return resolveShopDistrict(row) === filterDistrict;
}

export function extractAddressAreaLabel(row) {
  return resolveShopDistrict(row) || "-";
}
